import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { Spinner } from '@/components/ui/spinner'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: React.ReactNode
  spinnerSize?: React.ComponentProps<typeof Spinner>['size']
  spinnerClassName?: string
  spinnerPlacement?: 'start' | 'end'
}

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      spinnerSize = 'sm',
      spinnerClassName,
      spinnerPlacement = 'start',
      disabled,
      children,
      ...props
    }: ButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = Boolean(disabled || (loading && !asChild))
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          asChild && loading && 'pointer-events-none opacity-50'
        )}
        ref={ref}
        {...(!asChild ? { disabled: isDisabled } : {})}
        aria-busy={loading || undefined}
        data-loading={loading ? 'true' : undefined}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {spinnerPlacement === 'start' && loading && (
              <Spinner
                className={cn('mr-2 text-current', spinnerClassName)}
                size={spinnerSize}
              />
            )}
            <span className={cn('inline-flex items-center', loading && 'opacity-90')}>
              {loading ? (loadingText ?? children) : children}
            </span>
            {spinnerPlacement === 'end' && loading && (
              <Spinner
                className={cn('ml-2 text-current', spinnerClassName)}
                size={spinnerSize}
              />
            )}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants } 